// Ensure React is imported correctly with explicit namespace and destructured imports
import React from "react" 
import { useState, useEffect } from "react"

import type {
  ToastActionElement,
  ToastProps,
} from "@/components/ui/toast"

const TOAST_LIMIT = 5
const TOAST_REMOVE_DELAY = 5000
const DEFAULT_TOAST_DURATION = 4000  // Global default: 4 seconds

// Global SMS input detection for toast blocking
function isSMSInputActive(): boolean {
  const isSMSInputFocused = document.querySelector('[data-sms-input="true"]:focus')
  const isAnyTextareaFocused = document.activeElement?.tagName === 'TEXTAREA'
  const isInSMSPage = window.location.pathname.includes('/sms')
  
  return !!(isSMSInputFocused || (isInSMSPage && isAnyTextareaFocused))
}

type ToasterToast = ToastProps & {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  action?: ToastActionElement
}

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const

let count = 0

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString()
}

type ActionType = typeof actionTypes

type Action =
  | {
      type: ActionType["ADD_TOAST"]
      toast: ToasterToast
    }
  | {
      type: ActionType["UPDATE_TOAST"]
      toast: Partial<ToasterToast>
    }
  | {
      type: ActionType["DISMISS_TOAST"]
      toastId?: ToasterToast["id"]
    }
  | {
      type: ActionType["REMOVE_TOAST"]
      toastId?: ToasterToast["id"]
    }

interface State {
  toasts: ToasterToast[]
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId)
    dispatch({
      type: "REMOVE_TOAST",
      toastId: toastId,
    })
  }, TOAST_REMOVE_DELAY)

  toastTimeouts.set(toastId, timeout)
}

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      }

    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      }

    case "DISMISS_TOAST": {
      const { toastId } = action

      // ! Side effects ! - This could be extracted into a dismissToast() action,
      // but I'll keep it here for simplicity
      if (toastId) {
        addToRemoveQueue(toastId)
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id)
        })
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t
        ),
      }
    }
    case "REMOVE_TOAST":
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        }
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      }
  }
}

const listeners: Array<(state: State) => void> = []

let memoryState: State = { toasts: [] }

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action)
  listeners.forEach((listener) => {
    listener(memoryState)
  })
}

type Toast = Omit<ToasterToast, "id">

function toast({ ...props }: Toast) {
  // Block toast creation entirely when SMS input is active
  if (isSMSInputActive()) {
    console.log('Toast blocked at source - SMS input active:', props.title)
    return {
      id: 'blocked',
      dismiss: () => {},
      update: () => {}
    }
  }

  const id = genId()

  const update = (props: ToasterToast) =>
    dispatch({
      type: "UPDATE_TOAST",
      toast: { ...props, id },
    })
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id })

  // Apply default duration if none specified
  const toastWithDefaults = {
    ...props,
    duration: props.duration !== undefined ? props.duration : DEFAULT_TOAST_DURATION
  }

  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...toastWithDefaults,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss()
      },
    },
  })

  // Auto-dismiss after duration if specified (but not for Infinity)
  if (toastWithDefaults.duration && toastWithDefaults.duration > 0 && toastWithDefaults.duration !== Infinity) {
    setTimeout(() => {
      dismiss()
    }, toastWithDefaults.duration)
  }

  return {
    id: id,
    dismiss,
    update,
  }
}

function useToast() {
  // Create a safe version of hooks to prevent "null" errors
  // Wrap in try-catch to handle potential React context issues
  try {
    const [state, setState] = useState<State>(memoryState)

    // Remove state dependency which could cause unnecessary re-renders
    // and potentially issues with hook ordering
    useEffect(() => {
      listeners.push(setState)
      return () => {
        const index = listeners.indexOf(setState)
        if (index > -1) {
          listeners.splice(index, 1)
        }
      }
    }, []) // Empty dependency array - only run on mount/unmount
    
    return {
      ...state,
      toast,
      dismiss: (toastId?: string) => dispatch({ type: "DISMISS_TOAST", toastId }),
    }
  } catch (error) {
    // If hooks fail, return a default implementation that won't break the app
    console.error("Toast hook failed, returning fallback implementation:", error)
    return {
      toasts: [],
      toast,
      dismiss: (toastId?: string) => dispatch({ type: "DISMISS_TOAST", toastId }),
    }
  }
}

// Add a static dismiss method to toast for easier usage
toast.dismiss = (toastId?: string) => dispatch({ type: "DISMISS_TOAST", toastId });

export { useToast, toast }
